/**
 * Cargo-insurance premium helpers — shared by the booking page's CargoInsurance
 * card and the RFQ creator's RfqInsuranceCard so both read a Breeze quote the SAME
 * way (DRY). Keeping this in one place means a Breeze response-shape change is fixed
 * once, here, instead of in every consumer.
 *
 * Breeze's quote action returns the quote object as result[1]. The premium was
 * historically read as `result[1].premium_amount`; a Breeze API change surfaced it
 * under a sibling key / nesting, which rendered in the UI as "-". extractPremium()
 * resolves it from the known shapes and returns a finite Number (raw, pre-profit) or
 * null; withProfit() applies PrimeFreight's margin (percent with a flat minimum).
 */

// Parse a CRA build-time env var as a number, falling back when unset/blank/invalid.
const envNumber = (raw, fallback) => {
  if (raw == null || raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
};

// PF margin config — overridable per environment via CRA build-time env vars; the
// defaults encode the agreed rule (10%, with a $50 flat minimum). Set in Netlify:
//   REACT_APP_INSURANCE_PROFIT_PERCENT   e.g. 0.1  (= 10%)
//   REACT_APP_INSURANCE_PROFIT_MIN       e.g. 50   (flat USD floor on the profit)
// Clamped to >= 0 so a misconfigured (negative) env var can never reduce a charge.
export const INSURANCE_PROFIT_PERCENT = Math.max(0, envNumber(process.env.REACT_APP_INSURANCE_PROFIT_PERCENT, 0.1));
export const INSURANCE_PROFIT_MIN = Math.max(0, envNumber(process.env.REACT_APP_INSURANCE_PROFIT_MIN, 50));

// Breeze quote result_code values. Enum over string literals (no scattered
// "quote_created" matches).
export const INSURANCE_RESULT = Object.freeze({
  QUOTE_CREATED: "quote_created",
});

// Premium can arrive under any of these keys depending on the Breeze action
// version — ordered most-specific → most-generic.
const PREMIUM_KEYS = ["premium_amount", "premiumAmount", "premium", "total_premium"];

// Wrappers a quote may be nested under (Breeze has, at times, returned the quote
// one level down).
const QUOTE_WRAPPERS = ["quote", "data", "result"];

// Coerce to a usable Number — explicitly rejecting null/undefined/"" so a present
// but empty `premium_amount` doesn't read as 0 and mask a real sibling key.
const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Resolve the raw (pre-profit) premium from a Breeze quote object. Checks the flat
 * keys, then the same keys one level under a known wrapper, then the first option
 * when the quote is an array. Returns a finite Number or null.
 */
export const extractPremium = (quote) => {
  if (quote == null || typeof quote !== "object") return null;
  if (Array.isArray(quote)) return extractPremium(quote[0]);

  for (const key of PREMIUM_KEYS) {
    const direct = toFiniteNumber(quote[key]);
    if (direct != null) return direct;
  }
  for (const wrapper of QUOTE_WRAPPERS) {
    const found = extractPremium(quote[wrapper]);
    if (found != null) return found;
  }
  return null;
};

// PF profit on a raw premium: the GREATER of (percent × raw) or the flat minimum,
// so small premiums still clear the floor. Percent/min are clamped to >= 0 (a
// misconfigured negative can never reduce the charge) and the result is rounded to
// cents so it reconciles exactly with premium_amount. Returns null when no premium.
export const profitAmount = (
  rawPremium,
  { percent = INSURANCE_PROFIT_PERCENT, min = INSURANCE_PROFIT_MIN } = {}
) => {
  const base = toFiniteNumber(rawPremium);
  if (base == null) return null;
  const pct = Math.max(0, toFiniteNumber(percent) ?? 0);
  const floor = Math.max(0, toFiniteNumber(min) ?? 0);
  return Number(Math.max(base * pct, floor).toFixed(2));
};

// Customer-facing premium = raw insurer premium + PF profit, rounded to cents.
// Derived from the SAME profitAmount() so premium - raw == profit to the cent.
// Returns null when there is no raw premium to mark up.
export const withProfit = (rawPremium, opts) => {
  const base = toFiniteNumber(rawPremium);
  if (base == null) return null;
  return Number((base + profitAmount(base, opts)).toFixed(2));
};

export const isQuoteCreated = (quote) =>
  quote != null && quote.result_code === INSURANCE_RESULT.QUOTE_CREATED;

// A quote that yields a chargeable instant premium: created AND carries a number.
// Drives the Create Policy gate and the review-required nudge.
export const hasInstantPremium = (quote) =>
  isQuoteCreated(quote) && extractPremium(quote) != null;
